// Vuex module for chat state management

import axios from 'axios';

// Tracks chats currently being marked as seen to prevent concurrent duplicate calls
const markingSeenChats = new Set();

export default {
  namespaced: true,

  state: {
    chats: [],                    // All user's chats
    messages: {},                 // Messages by chat_id: { chatId: [messages...] }
    selectedChatId: null,
    onlineUsers: [],              // Array of user IDs who are online
    typingUsers: {},              // { chatId: [userId1, userId2...] }
    loading: false,
    loadingMessages: false,
    connected: false,
    error: null
  },

  getters: {
    selectedChat: (state) => {
      return state.chats.find(chat => chat.id === state.selectedChatId);
    },

    getChatMessages: (state) => (chatId) => {
      return state.messages[chatId] || [];
    },

    getUnreadCount: (state, getters, rootState) => (chatId) => {
      const messages = state.messages[chatId] || [];
      const currentUserId = rootState.auth.user?.id;
      if (!currentUserId) return 0;

      return messages.filter(m =>
        !m.seen && m.sender_id !== currentUserId
      ).length;
    },

    getTotalUnreadCount: (state, getters, rootState) => {
      const currentUserId = rootState.auth.user?.id;
      if (!currentUserId) return 0;

      let total = 0;
      Object.values(state.messages).forEach(chatMessages => {
        total += chatMessages.filter(m =>
          !m.seen && m.sender_id !== currentUserId
        ).length;
      });
      return total;
    },

    isUserOnline: (state) => (userId) => {
      return state.onlineUsers.includes(userId);
    },

    getTypingUsers: (state) => (chatId) => {
      return state.typingUsers[chatId] || [];
    },

    sortedChats: (state) => {
      return [...state.chats].sort((a, b) => {
        const getLastMessageTime = (chat) => {
          const messages = state.messages[chat.id] || [];
          if (messages.length > 0) {
            return new Date(messages[messages.length - 1].created_at);
          }
          return new Date(chat.created_at);
        };

        return getLastMessageTime(b) - getLastMessageTime(a);
      });
    }
  },

  mutations: {
    SET_CHATS(state, chats) {
      state.chats = chats;
    },

    ADD_CHAT(state, chat) {
      const index = state.chats.findIndex(c => c.id === chat.id);
      if (index === -1) {
        state.chats.unshift(chat);
      } else {
        state.chats.splice(index, 1, chat);
      }
    },

    REMOVE_CHAT(state, chatId) {
      state.chats = state.chats.filter(c => c.id !== chatId);
      delete state.messages[chatId];
    },

    SET_MESSAGES(state, { chatId, messages }) {
      state.messages = {
        ...state.messages,
        [chatId]: messages
      };
    },

    PREPEND_MESSAGES(state, { chatId, messages }) {
      if (!state.messages[chatId]) {
        state.messages[chatId] = [];
      }
      state.messages[chatId] = [...messages, ...state.messages[chatId]];
    },

    ADD_MESSAGE(state, message) {
      const chatId = message.chat_id;
      if (!state.messages[chatId]) {
        state.messages = {
          ...state.messages,
          [chatId]: []
        };
      }

      // Check for duplicate
      const exists = state.messages[chatId].find(m => m.id === message.id);
      if (!exists) {
        // Create a new array to trigger reactivity
        state.messages[chatId] = [...state.messages[chatId], message];
      }
    },

    UPDATE_MESSAGE(state, message) {
      const chatId = message.chat_id;
      if (state.messages[chatId]) {
        const index = state.messages[chatId].findIndex(m => m.id === message.id);
        if (index !== -1) {
          // Create a new array to trigger reactivity
          state.messages[chatId] = [
            ...state.messages[chatId].slice(0, index),
            message,
            ...state.messages[chatId].slice(index + 1)
          ];
        }
      }
    },

    REMOVE_MESSAGE(state, { chatId, messageId }) {
      if (state.messages[chatId]) {
        state.messages[chatId] = state.messages[chatId].filter(m => m.id !== messageId);
      }
    },

    MARK_MESSAGES_AS_SEEN(state, { chatId, messageIds }) {
      if (state.messages[chatId]) {
        state.messages[chatId] = state.messages[chatId].map(message => {
          if (messageIds.includes(message.id)) {
            return {
              ...message,
              seen: true,
              seen_at: new Date().toISOString()
            };
          }
          return message;
        });
      }
    },

    MARK_ALL_MESSAGES_AS_SEEN(state, chatId) {
      if (state.messages[chatId]) {
        state.messages[chatId] = state.messages[chatId].map(message => ({
          ...message,
          seen: true,
          seen_at: new Date().toISOString()
        }));
      }
    },

    SET_SELECTED_CHAT(state, chatId) {
      state.selectedChatId = chatId;
    },

    SET_ONLINE_USERS(state, users) {
      state.onlineUsers = users;
    },

    ADD_ONLINE_USER(state, userId) {
      if (!state.onlineUsers.includes(userId)) {
        state.onlineUsers.push(userId);
      }
    },

    REMOVE_ONLINE_USER(state, userId) {
      state.onlineUsers = state.onlineUsers.filter(id => id !== userId);
    },

    SET_TYPING_USERS(state, { chatId, users }) {
      state.typingUsers = {
        ...state.typingUsers,
        [chatId]: users
      };
    },

    ADD_TYPING_USER(state, { chatId, userId }) {
      if (!state.typingUsers[chatId]) {
        state.typingUsers = {
          ...state.typingUsers,
          [chatId]: []
        };
      }
      if (!state.typingUsers[chatId].includes(userId)) {
        state.typingUsers[chatId].push(userId);
      }
    },

    REMOVE_TYPING_USER(state, { chatId, userId }) {
      if (state.typingUsers[chatId]) {
        state.typingUsers[chatId] = state.typingUsers[chatId].filter(id => id !== userId);
      }
    },

    SET_LOADING(state, loading) {
      state.loading = loading;
    },

    SET_LOADING_MESSAGES(state, loading) {
      state.loadingMessages = loading;
    },

    SET_CONNECTED(state, connected) {
      state.connected = connected;
    },

    SET_ERROR(state, error) {
      state.error = error;
    }
  },

  actions: {
    // Fetch all chats
    async fetchChats({ commit }) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.get('/chats');
        const chats = response.data.data.filter(c => c.is_active && !c.is_deleted);
        commit('SET_CHATS', chats);
        return chats;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Fetch messages for a specific chat
    async fetchMessages({ commit }, { chatId, page = 1, rowsPerPage = 50 }) {
      commit('SET_LOADING_MESSAGES', true);
      try {
        const response = await axios.get('/chat_messages', {
          params: { chat_id: chatId, page, rowsPerPage }
        });
        // Backend already returns messages in chronological order (oldest-first)
        const messages = response.data.data || [];

        if (page === 1) {
          commit('SET_MESSAGES', { chatId, messages });
        } else {
          // Load more: older messages go above existing ones
          commit('PREPEND_MESSAGES', { chatId, messages });
        }

        return {
          messages,
          hasMore: response.data.has_more || false
        };
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING_MESSAGES', false);
      }
    },

    // Create private chat
    async createPrivateChat({ commit, dispatch }, userId) {
      try {
        const response = await axios.post('/chats', {
          user_id: userId,
          is_group: false
        });
        const chat = response.data.data;
        commit('ADD_CHAT', chat);

        // Fetch messages for this chat
        await dispatch('fetchMessages', { chatId: chat.id });

        return chat;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      }
    },

    // Create group chat
    async createGroupChat({ commit, dispatch }, { name, userIds, storeId }) {
      try {
        const response = await axios.post('/chats/group_chats', {
          name,
          user_ids: userIds,
          store_id: storeId
        });
        const chat = response.data.data;
        commit('ADD_CHAT', chat);

        // Fetch messages for this chat
        await dispatch('fetchMessages', { chatId: chat.id });

        return chat;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      }
    },

    // Send message
    async sendMessage({ commit, rootState }, formData) {
      // Create temporary message for optimistic UI
      const chatId = formData.get('chat_id');
      const messageText = formData.get('message') || null;
      const fileEntry = formData.get('file_path');
      const blobUrl = fileEntry ? URL.createObjectURL(fileEntry) : null;
      const tempMessage = {
        id: `temp-${Date.now()}`,
        message: messageText,
        file_path: blobUrl,
        file_url: blobUrl,
        filename: fileEntry ? (formData.get('filename') || fileEntry.name) : null,
        sender_id: rootState.auth.user.id,
        chat_id: chatId,
        created_at: new Date().toISOString(),
        sending: true,
        error: false,
        seen: false,
        sender: rootState.auth.user
      };

      commit('ADD_MESSAGE', tempMessage);

      try {
        const response = await axios.post('/chat_messages', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Remove temp message and add the confirmed message from server
        commit('REMOVE_MESSAGE', { chatId, messageId: tempMessage.id });
        const message = response.data.data;
        if (message && message.id) {
          commit('ADD_MESSAGE', { ...message, chat_id: parseInt(chatId) });
        }

        return response.data;
      } catch (error) {
        // Mark message as failed
        tempMessage.sending = false;
        tempMessage.error = true;
        commit('UPDATE_MESSAGE', tempMessage);

        commit('SET_ERROR', error.message);
        throw error;
      }
    },

    // Retry sending a failed message
    async retryMessage({ commit, dispatch }, { chatId, message }) {
      // Remove the failed message
      commit('REMOVE_MESSAGE', { chatId, messageId: message.id });

      // Create new FormData and resend
      const formData = new FormData();
      formData.append('message', message.message);
      formData.append('chat_id', chatId);

      // Reconstruct other fields if needed
      const chat = this.state.chat.chats.find(c => c.id === chatId);
      if (chat) {
        if (chat.is_group) {
          formData.append('is_group', true);
          if (chat.store_id) formData.append('store_id', chat.store_id);
        } else {
          const otherUser = chat.users.find(u => u.id !== this.state.auth.user.id);
          if (otherUser) formData.append('user_id', otherUser.id);
        }
      }

      await dispatch('sendMessage', formData);
    },

    // Mark all messages in chat as seen
    async markChatAsSeen({ commit, rootState }, chatId) {
      // Skip if already in-flight for this chat
      if (markingSeenChats.has(chatId)) return;

      // Skip if there are no unseen messages from others
      const messages = rootState.chat.messages[chatId] || [];
      const unseenMessages = messages.filter(
        m => !m.seen && m.sender_id !== rootState.auth.user?.id
      );
      if (unseenMessages.length === 0) return;

      markingSeenChats.add(chatId);
      try {
        await axios.post(`/chat_messages/bulk-mark-seen/${chatId}`);
        commit('MARK_ALL_MESSAGES_AS_SEEN', chatId);
      } catch (error) {
        console.error('Failed to mark messages as seen:', error);
      } finally {
        markingSeenChats.delete(chatId);
      }
    },

    // Add members to group chat
    async addGroupMembers({ commit }, { chatId, userIds }) {
      try {
        const response = await axios.post('/chats/group_chat_add_members', {
          chat_id: chatId,
          user_ids: userIds
        });
        const chat = response.data.data;
        commit('ADD_CHAT', chat);
        return chat;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      }
    },

    // Remove member from group chat
    async removeGroupMember({ commit }, { chatId, userId }) {
      try {
        const response = await axios.post('/chats/group_chat_remove-member', {
          chat_id: chatId,
          user_id: userId
        });
        const chat = response.data.data;
        commit('ADD_CHAT', chat);
        return chat;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      }
    },

    // Leave group chat
    async leaveGroupChat({ commit }, chatId) {
      try {
        await axios.post('/chats/group_chat_leave', {
          chat_id: chatId
        });
        commit('REMOVE_CHAT', chatId);
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      }
    },

    // WebSocket event handlers
    handleNewMessage({ commit, state }, message) {
      commit('ADD_MESSAGE', message);

      // Mark-as-seen is handled by IntersectionObserver in MessageThread.vue
      // when the message becomes visible — no setTimeout needed here.

      // Update chat's last message in chat list
      const chat = state.chats.find(c => c.id === message.chat_id);
      if (chat) {
        commit('ADD_CHAT', { ...chat, updated_at: message.created_at });
      }
    },

    handleTypingStart({ commit, rootState }, { chatId, userId }) {
      // Don't show typing for own messages
      if (userId === rootState.auth.user.id) return;

      commit('ADD_TYPING_USER', { chatId, userId });

      // Auto-remove after 5 seconds
      setTimeout(() => {
        commit('REMOVE_TYPING_USER', { chatId, userId });
      }, 5000);
    },

    handleTypingStop({ commit }, { chatId, userId }) {
      commit('REMOVE_TYPING_USER', { chatId, userId });
    },

    handleUserOnline({ commit }, userId) {
      commit('ADD_ONLINE_USER', userId);
    },

    handleUserOffline({ commit }, userId) {
      commit('REMOVE_ONLINE_USER', userId);
    }
  }
};
